import { useState } from 'react';
import {
  Accordion,
  DismissibleAlert,
  Drawer,
  Dropdown,
  MotionPreference,
  Pagination,
  RangeField,
  Skeleton,
  Stepper,
  Tabs,
  Tooltip,
} from '../../components/ui/Interactive';
import { Avatar, Badge, Card, Progress, SectionTitle, Toggle } from '../../components/ui/Primitives';
import { useToast } from '../../components/ui/ToastProvider';

const tabItems = [
  {
    id: 'overview',
    label: 'Overview',
    content: <p>High-signal controls use concise labels, visible state and immediate feedback.</p>,
  },
  {
    id: 'behavior',
    label: 'Behavior',
    content: <p>Motion is subtle, reversible and disabled globally when reduced motion is selected.</p>,
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    content: <p>Interactive elements expose keyboard focus, labels, roles and current state.</p>,
  },
] as const;

export function DesignSystemPage() {
  const { pushToast } = useToast();
  const [sampleAction, setSampleAction] = useState('No specimen action yet.');
  const [activeTab, setActiveTab] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [density, setDensity] = useState(64);
  const [page, setPage] = useState(2);
  const [loading, setLoading] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(false);

  function invoke(label: string) {
    setSampleAction(label + ' action invoked.');
    pushToast(label + ' action', {
      message: 'Interactive feedback is wired through the global notification system.',
      tone: label === 'Destructive' ? 'danger' : 'success',
    });
  }

  return (
    <div className="page-stack design-system-page">
      <div className="hero-row">
        <div>
          <span className="page-kicker">Foundation</span>
          <h1>Interactive design system</h1>
          <p className="page-intro">
            A living catalog of production-ready controls, motion, states and composition patterns.
            Every specimen below is interactive.
          </p>
        </div>
        <div className="hero-actions">
          <MotionPreference />
          <button className="btn secondary" onClick={() => setDrawerOpen(true)}>Open component drawer</button>
        </div>
      </div>

      <nav className="component-index" aria-label="Component index">
        <a href="#feedback">Feedback</a>
        <a href="#navigation">Navigation</a>
        <a href="#disclosure">Disclosure</a>
        <a href="#inputs">Inputs</a>
        <a href="#loading">Loading</a>
        <a href="#data">Data</a>
      </nav>

      <DismissibleAlert tone="success" title="Reactive component suite online">
        Tabs, drawers, menus, feedback, loading states and motion preferences are all running from reusable components.
      </DismissibleAlert>

      <Card>
        <SectionTitle eyebrow="Tokens" title="Color & surface hierarchy" />
        <div className="swatch-grid">
          {['--bg','--surface','--surface-2','--surface-3','--text','--muted','--accent','--positive','--warning','--danger'].map((value) => (
            <div key={value}>
              <span className="swatch" style={{ background: 'var(' + value + ')' }} />
              <code>{value}</code>
            </div>
          ))}
        </div>
      </Card>

      <div className="two-grid" id="feedback">
        <Card>
          <SectionTitle title="Buttons, tooltips & toast feedback" />
          <div className="component-row">
            <button className="btn primary" onClick={() => invoke('Primary')}>Primary</button>
            <button className="btn secondary" onClick={() => invoke('Secondary')}>Secondary</button>
            <button className="btn ghost" onClick={() => invoke('Ghost')}>Ghost</button>
            <button className="btn danger" onClick={() => invoke('Destructive')}>Destructive</button>
            <Tooltip label="Tooltips open on hover and keyboard focus.">
              <button className="icon-btn" type="button" aria-label="Tooltip specimen">?</button>
            </Tooltip>
          </div>
          <p className="inline-note">{sampleAction}</p>
          <div className="component-row">
            <Badge tone="positive">Success</Badge>
            <Badge tone="info">Info</Badge>
            <Badge tone="warning">Warning</Badge>
            <Badge tone="danger">Danger</Badge>
            <Badge>Neutral</Badge>
          </div>
        </Card>

        <Card>
          <SectionTitle title="Dropdown menu" />
          <Dropdown
            label="Run action"
            items={[
              { id: 'duplicate', label: 'Duplicate workspace', detail: 'Creates a local copy' },
              { id: 'export', label: 'Export configuration', detail: 'Downloads portable state' },
              { id: 'archive', label: 'Archive workspace', detail: 'Moves it out of active views' },
            ]}
            onSelect={(id) => {
              setSampleAction('Menu action: ' + id);
              pushToast('Menu action completed', { message: id, tone: 'info' });
            }}
          />
          <p className="component-help">The menu closes on outside click and after a selection.</p>
        </Card>
      </div>

      <div className="two-grid" id="navigation">
        <Card>
          <SectionTitle title="Tabs" />
          <Tabs items={tabItems} value={activeTab} onChange={setActiveTab} />
        </Card>

        <Card>
          <SectionTitle title="Stepper" />
          <Stepper
            steps={['Account', 'Workspace', 'Integrations', 'Launch']}
            value={step}
            onChange={setStep}
          />
          <div className="stepper-summary">
            <span>Current phase</span>
            <strong>{['Account', 'Workspace', 'Integrations', 'Launch'][step]}</strong>
          </div>
        </Card>
      </div>

      <div className="two-grid" id="disclosure">
        <Card>
          <SectionTitle title="Accordion" />
          <Accordion
            items={[
              { title: 'What is persisted?', content: <p>Projects, messages, settings and workspace state are stored locally.</p> },
              { title: 'Can motion be reduced?', content: <p>Yes. The preference above disables non-essential transitions and animations globally.</p> },
              { title: 'Does it work with a backend?', content: <p>Yes. The state layer is isolated so a network adapter can replace local persistence.</p> },
            ]}
          />
        </Card>

        <Card>
          <SectionTitle title="Drawer & overlay" />
          <p className="component-help">Drawers trap the visual workflow without navigating away from the current page.</p>
          <button className="btn primary" onClick={() => setDrawerOpen(true)}>Configure interface</button>
        </Card>
      </div>

      <div className="two-grid" id="inputs">
        <Card>
          <SectionTitle title="Reactive inputs" />
          <div className="form-stack">
            <label>Workspace name<input defaultValue="Northstar Research" /></label>
            <label>Region<select defaultValue="ca"><option value="ca">Canada Central</option><option value="us">US East</option></select></label>
            <label>Description<textarea defaultValue="High-signal operational workspace for product and engineering." /></label>
            <Toggle label="Require approval before production" checked={approvalRequired} onChange={setApprovalRequired} />
            <RangeField label="Interface density" value={density} min={40} max={100} onChange={setDensity} />
          </div>
          <div className="density-preview" style={{ gap: Math.max(5, 22 - density / 6) }}>
            <span /><span /><span />
          </div>
        </Card>

        <Card>
          <SectionTitle title="Identity & status" />
          <div className="avatar-showcase">
            <Avatar name="Avery Stone" size="lg" />
            <Avatar name="Mina Park" />
            <Avatar name="Noah Chen" size="sm" />
          </div>
          <div className="component-row">
            <span className="presence"><i className="status-dot ok" /> Online</span>
            <span className="presence"><i className="status-dot warn" /> Focus</span>
            <span className="presence"><i className="status-dot danger" /> Incident</span>
          </div>
          <div className="progress-example">
            <span>Interface readiness <b>{density}%</b></span>
            <Progress value={density} />
          </div>
        </Card>
      </div>

      <div className="two-grid" id="loading">
        <Card>
          <SectionTitle title="Loading & skeleton state" />
          {loading ? <Skeleton lines={4} /> : (
            <div className="loaded-state">
              <Avatar name="Mina Park" />
              <div><strong>Content loaded</strong><span>Reactive loading state has resolved.</span></div>
            </div>
          )}
          <button
            className="btn secondary"
            onClick={() => {
              setLoading(true);
              window.setTimeout(() => setLoading(false), 1200);
            }}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Simulate loading'}
          </button>
        </Card>

        <Card>
          <SectionTitle title="Pagination" />
          <div className="pagination-preview">
            <strong>Result page {page}</strong>
            <span>Showing records {(page - 1) * 10 + 1}–{page * 10}</span>
          </div>
          <Pagination pages={5} value={page} onChange={setPage} />
        </Card>
      </div>

      <div id="data">
        <Card>
          <SectionTitle title="Table pattern" />
        <div className="table-scroll">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Role</th><th>Status</th><th>Permission</th><th>Last active</th></tr></thead>
            <tbody>
              {[
                ['Avery Stone','Engineer','Online','Admin','Now'],
                ['Mina Park','Designer','Online','Editor','2m'],
                ['Noah Chen','Analyst','Focus','Viewer','8m'],
              ].map((row) => (
                <tr key={row[0]}>
                  {row.map((value, index) => <td key={value}>{index === 2 ? <Badge tone="positive">{value}</Badge> : value}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      </div>

      <Drawer open={drawerOpen} title="Interface configuration" onClose={() => setDrawerOpen(false)}>
        <div className="form-stack">
          <MotionPreference />
          <RangeField label="Density" value={density} min={40} max={100} onChange={setDensity} />
          <Toggle label="Production approval gate" checked={approvalRequired} onChange={setApprovalRequired} />
          <button
            className="btn primary"
            onClick={() => {
              setDrawerOpen(false);
              pushToast('Interface preferences applied', { tone: 'success' });
            }}
          >
            Apply preferences
          </button>
        </div>
      </Drawer>
    </div>
  );
}
